import { useState } from 'react';
import { MapPin, Radio } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SegmentChips } from '@/components/common/SegmentChips';
import { segParam } from '@/lib/segments';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { HorizontalBars } from '@/components/charts/HorizontalBars';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

export default function Operations() {
  const { body, clearAll, parrainageOn, parrainageSeuil } = useFilters();
  const [seg, setSeg] = useState('ensemble');
  const segBody = parrainageOn ? { ...body, segment: segParam(seg), parrainage_seuil: parrainageSeuil } : body;
  const codes = usePost('/analytics/breakdown', { ...segBody, dim: 'campaign_code' });
  const canaux = usePost('/analytics/breakdown', { ...segBody, dim: 'canal' });
  const regions = usePost('/analytics/breakdown', { ...segBody, dim: 'region' });
  const items = codes.data?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader title="Opérations" icon={MapPin}
        subtitle="Vue opérationnelle terrain : canaux, régions et missions" />
      {parrainageOn && (
        <SegmentChips single value={seg} onChange={setSeg} testid="operations-segment" />
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SectionCard title="Répartition par canal" subtitle="BS validés par canal de collecte">
          {canaux.isLoading ? <CardSkeleton /> : (canaux.data?.items?.length
            ? <HorizontalBars data={canaux.data.items.map((c) => ({ key: c.key, value: c.bs_reel }))} formatTip={(v) => `${fmtInt(v)} BS`} />
            : <EmptyState onClear={clearAll} />)}
        </SectionCard>
        <SectionCard title="Répartition par région" subtitle="BS validés par région">
          {regions.isLoading ? <CardSkeleton /> : (() => {
            const rows = (regions.data?.items || [])
              .filter((c) => c.key && c.key !== 'Non renseigné' && c.bs_reel > 0)
              .slice(0, 8);
            return rows.length
              ? <HorizontalBars data={rows.map((c) => ({ key: c.key, value: c.bs_reel }))}
                  height={Math.max(300, rows.length * 54)} yWidth={200} formatTip={(v) => `${fmtInt(v)} BS`} />
              : <EmptyState onClear={clearAll} />;
          })()}
        </SectionCard>
      </div>

      <SectionCard title="Détail des missions" subtitle="Toutes les opérations par code campagne" bodyClassName="!px-0">
        {codes.isLoading ? <div className="px-5"><CardSkeleton /></div> : items.length === 0 ? <div className="px-5"><EmptyState onClear={clearAll} /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Code</TableHead>
                  <TableHead className="text-right">BS signés</TableHead>
                  <TableHead className="text-right">BS validés</TableHead>
                  <TableHead className="text-right">Transfo</TableHead>
                  <TableHead className="text-right">Don moyen</TableHead>
                  <TableHead className="text-right">Heures</TableHead>
                  <TableHead className="text-right">Prod.</TableHead>
                  <TableHead className="text-right">Âge moy.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.key} data-testid="operation-row">
                    <TableCell className="font-mono text-xs font-semibold accent-text">
                      {c.key}
                      {(c.canal || c.region) && (
                        <span className="mt-0.5 block font-sans text-[10px] font-normal text-muted-foreground">
                          {[c.canal, c.region].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular">{fmtInt(c.bs_rue)}</TableCell>
                    <TableCell className="text-right tabular font-medium">{fmtInt(c.bs_reel)}</TableCell>
                    <TableCell className="text-right tabular">{fmtPct(c.taux_transfo)}</TableCell>
                    <TableCell className="text-right tabular">{fmtEur(c.don_moyen)}</TableCell>
                    <TableCell className="text-right tabular">{fmtInt(c.heures)}</TableCell>
                    <TableCell className="text-right tabular">{fmtNum(c.productivite, 2)}</TableCell>
                    <TableCell className="text-right tabular">{fmtNum(c.age_moyen, 1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
